import React, { useState, useEffect, useCallback } from "react";
import {
  Award, Search, User, Send, X, Check,
  Trophy, Star, Crown, Flame, Heart
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

interface Profile {
  id: string;
  name: string;
  photo_url: string | null;
}

interface BadgeType {
  id: string;
  name: string;
  icon: React.ElementType;
  color: string;
  description: string;
}

const BADGE_TYPES: BadgeType[] = [
  {
    id: "most_requested",
    name: "Most Requested Booth",
    icon: Flame,
    color: "text-orange-500",
    description: "Awarded to hosts with the most requested booths",
  },
  {
    id: "top_host",
    name: "Top Host",
    icon: Crown,
    color: "text-amber-500",
    description: "Awarded to outstanding exhibition hosts",
  },
  {
    id: "popular_exhibitor",
    name: "Popular Exhibitor",
    icon: Star,
    color: "text-pink-500",
    description: "Awarded to exhibitors who received the most pops",
  },
  {
    id: "community_favorite",
    name: "Community Favorite",
    icon: Heart,
    color: "text-red-500",
    description: "Voted by the community as a favorite",
  },
  {
    id: "first_booth",
    name: "First Booth Creator",
    icon: Trophy,
    color: "text-blue-500",
    description: "Created their first exhibition booth",
  },
];

const AdminBadgeAward: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<Profile[]>([]);
  const [selectedUser, setSelectedUser] = useState<Profile | null>(null);
  const [selectedBadge, setSelectedBadge] = useState<BadgeType | null>(null);
  const [customMessage, setCustomMessage] = useState("");
  const [rank, setRank] = useState<number | "">("");
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [recentAwards, setRecentAwards] = useState<any[]>([]);

  // Search for users
  const searchUsers = useCallback(async (query: string) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }

    setSearchLoading(true);
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, name, photo_url")
        .ilike("name", `%${query}%`)
        .limit(10);

      if (error) throw error;
      setSearchResults(data || []);
    } catch (error) {
      console.error("Error searching users:", error);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      searchUsers(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, searchUsers]);

  // Fetch recent awards
  const fetchRecentAwards = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from("exhibition_badges")
        .select(`
          *,
          profiles:user_id (
            name,
            photo_url
          )
        `)
        .order("earned_at", { ascending: false })
        .limit(5);

      if (error) throw error;
      setRecentAwards(data || []);
    } catch (error) {
      console.error("Error fetching recent awards:", error);
    }
  }, []);

  useEffect(() => {
    fetchRecentAwards();
  }, [fetchRecentAwards]);

  // Award badge
  const handleAwardBadge = async () => {
    if (!selectedUser || !selectedBadge) {
      toast.error("Please select a user and badge type");
      return;
    }

    setLoading(true);
    try {
      // Insert badge
      const { error: badgeError } = await supabase
        .from("exhibition_badges")
        .insert({
          user_id: selectedUser.id,
          badge_type: selectedBadge.id,
          rank: rank || null,
          city_name: customMessage || null,
        });

      if (badgeError) throw badgeError;

      // Create notification for user
      const { error: notifError } = await supabase
        .from("notifications")
        .insert({
          user_id: selectedUser.id,
          type: "badge_earned",
          title: `🏆 You earned a badge!`,
          body: `Congratulations! You've been awarded the "${selectedBadge.name}" badge${rank ? ` (Rank #${rank})` : ""}.`,
          data: {
            badge_type: selectedBadge.id,
            badge_name: selectedBadge.name,
            rank: rank || null,
            custom_message: customMessage,
          },
        });

      if (notifError) {
        console.error("Error creating notification:", notifError);
      }

      toast.success(`Badge awarded to ${selectedUser.name}!`);
      
      // Reset form
      setSelectedUser(null);
      setSelectedBadge(null);
      setCustomMessage("");
      setRank("");
      setSearchQuery("");
      setIsOpen(false);
      
      // Refresh recent awards
      fetchRecentAwards();
    } catch (error) {
      console.error("Error awarding badge:", error);
      toast.error("Failed to award badge");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSelectedUser(null);
    setSelectedBadge(null);
    setCustomMessage("");
    setRank("");
    setSearchQuery("");
    setSearchResults([]);
  };

  return (
    <div className="rounded-xl bg-white/50 dark:bg-gray-900/50 backdrop-blur-xl border border-white/60 dark:border-gray-700/60 overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-white/40 dark:border-gray-700/40">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Award className="w-5 h-5 text-amber-500" />
            <h3 className="font-bold text-foreground">Badge Awards</h3>
          </div>
          
          <Dialog open={isOpen} onOpenChange={(open) => {
            setIsOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button size="sm" className="gap-2 rounded-full">
                <Award className="w-4 h-4" />
                Award Badge
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-amber-500" />
                  Award Badge to User
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-4 mt-4">
                {/* User Search */}
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Search User
                  </label>
                  <div className="relative mt-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Type to search users..."
                      className="pl-9"
                    />
                  </div>

                  {/* Search Results */}
                  {searchResults.length > 0 && !selectedUser && (
                    <div className="mt-2 border rounded-lg divide-y max-h-40 overflow-y-auto">
                      {searchResults.map((user) => (
                        <button
                          key={user.id}
                          onClick={() => {
                            setSelectedUser(user);
                            setSearchQuery("");
                            setSearchResults([]);
                          }}
                          className="w-full flex items-center gap-3 p-2 hover:bg-muted/50 transition-colors"
                        >
                          {user.photo_url ? (
                            <img
                              src={user.photo_url}
                              alt={user.name}
                              className="w-8 h-8 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                              <User className="w-4 h-4 text-muted-foreground" />
                            </div>
                          )}
                          <span className="font-medium text-foreground">
                            {user.name}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}

                  {searchLoading && (
                    <p className="text-xs text-muted-foreground mt-2">Searching...</p>
                  )}
                </div>

                {/* Selected User */}
                {selectedUser && (
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-primary/10 border border-primary/20">
                    {selectedUser.photo_url ? (
                      <img
                        src={selectedUser.photo_url}
                        alt={selectedUser.name}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                        <User className="w-5 h-5 text-muted-foreground" />
                      </div>
                    )}
                    <div className="flex-1">
                      <p className="font-medium text-foreground">{selectedUser.name}</p>
                      <p className="text-xs text-muted-foreground">Selected user</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => setSelectedUser(null)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                )}

                {/* Badge Type Selection */}
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Badge Type
                  </label>
                  <div className="grid grid-cols-1 gap-2 mt-2">
                    {BADGE_TYPES.map((badge) => {
                      const Icon = badge.icon;
                      const isSelected = selectedBadge?.id === badge.id;
                      
                      return (
                        <button
                          key={badge.id}
                          onClick={() => setSelectedBadge(badge)}
                          className={cn(
                            "flex items-center gap-3 p-3 rounded-lg border transition-all text-left",
                            isSelected
                              ? "bg-primary/10 border-primary"
                              : "bg-muted/30 border-muted/50 hover:border-muted"
                          )}
                        >
                          <Icon className={cn("w-5 h-5", badge.color)} />
                          <div className="flex-1">
                            <p className="font-medium text-foreground text-sm">
                              {badge.name}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {badge.description}
                            </p>
                          </div>
                          {isSelected && (
                            <Check className="w-4 h-4 text-primary" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Rank (optional) */}
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Rank (optional)
                  </label>
                  <Input
                    type="number"
                    min={1}
                    max={100}
                    value={rank}
                    onChange={(e) => setRank(e.target.value ? parseInt(e.target.value) : "")}
                    placeholder="e.g., 1, 2, 3..."
                    className="mt-1"
                  />
                </div>

                {/* Custom Message */}
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Custom Note (optional)
                  </label>
                  <Textarea
                    value={customMessage}
                    onChange={(e) => setCustomMessage(e.target.value)}
                    placeholder="Additional context for the award..."
                    className="mt-1"
                    rows={2}
                  />
                </div>

                {/* Actions */}
                <div className="flex gap-2 pt-4">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => {
                      setIsOpen(false);
                      resetForm();
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    className="flex-1"
                    onClick={handleAwardBadge}
                    disabled={!selectedUser || !selectedBadge || loading}
                  >
                    {loading ? (
                      <>
                        <div className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin mr-2" />
                        Awarding...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Award Badge
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Recent Awards */}
      <div className="p-4">
        <h4 className="text-sm font-semibold text-muted-foreground mb-3">
          Recent Awards
        </h4>
        {recentAwards.length === 0 ? (
          <div className="text-center py-6">
            <Award className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">No badges awarded yet</p>
          </div>
        ) : (
          <div className="space-y-2">
            {recentAwards.map((award) => {
              const badgeType = BADGE_TYPES.find(b => b.id === award.badge_type);
              const Icon = badgeType?.icon || Award;
              
              return (
                <div
                  key={award.id}
                  className="flex items-center gap-3 p-2 rounded-lg bg-muted/30"
                >
                  <div className={cn(
                    "w-8 h-8 rounded-full flex items-center justify-center",
                    "bg-gradient-to-br from-amber-400 to-orange-500"
                  )}>
                    <Icon className="w-4 h-4 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-foreground text-sm truncate">
                      {award.profiles?.name || "Unknown"}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                      {badgeType?.name || award.badge_type}
                      {award.rank && ` • Rank #${award.rank}`}
                    </p>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {new Date(award.earned_at).toLocaleDateString()}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminBadgeAward;
